import ThemeToggle from '@/Components/ThemeToggle';
import { Link, usePage } from '@inertiajs/react';
import { useState, useEffect, createContext, useContext } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import LoginModal from '@/Components/LoginModal';
import RegisterModal from '@/Components/RegisterModal';
import ForgotPasswordModal from '@/Components/ForgotPasswordModal';
import Footer from '@/Components/Frontend/Footer';
import Header from '@/Components/Frontend/Header';

import { Toaster, toast } from 'react-hot-toast';

export const ModalContext = createContext();

export function useModals() {
    return useContext(ModalContext);
}

export default function MainLayout({ auth, children, showFooter = true, showHeader = true, headerOffsetClass = '' }) {
    const { flash, auth: pageAuth, is_tenant_expired } = usePage().props;
    const [isScrolled, setIsScrolled] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);

    useEffect(() => {
        if (flash?.success) {
            toast.success(flash.success);
        }
        if (flash?.error) {
            toast.error(flash.error);
        }
        if (flash?.message) {
            toast(flash.message);
        }
    }, [flash]);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleOpenLogin = () => openLogin();
        window.addEventListener('open-login-modal', handleOpenLogin);
        return () => window.removeEventListener('open-login-modal', handleOpenLogin);
    }, []);

    const scrollToSection = (e, targetId) => {
        e.preventDefault();
        const element = document.querySelector(targetId);
        if (element) {
            const headerOffset = 80; // Height of sticky header
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const openLogin = () => {
        setIsRegisterModalOpen(false);
        setIsForgotPasswordModalOpen(false);
        setIsLoginModalOpen(true);
    };

    const openRegister = () => {
        // If already logged in, go to dashboard instead of opening signup
        if (pageAuth?.user) {
            window.location.href = '/manager/dashboard';
            return;
        }
        setIsLoginModalOpen(false);
        setIsForgotPasswordModalOpen(false);
        setIsRegisterModalOpen(true);
    };

    const openForgotPassword = () => {
        setIsLoginModalOpen(false);
        setIsRegisterModalOpen(false);
        setIsForgotPasswordModalOpen(true);
    };

    return (
        <ModalContext.Provider value={{ openLogin, openRegister, openForgotPassword }}>
            <div className="bg-background-light dark:bg-background-dark text-slate-900 dark:text-white min-h-screen flex flex-col font-display transition-colors duration-300">
                {is_tenant_expired && (
                    <div className="bg-red-500 text-white text-center py-2 px-4 text-sm font-bold z-50 relative">
                        This platform's agency API subscription has expired. Please renew.
                    </div>
                )}
                {showHeader && <Header />}

                <main className={`${showHeader ? headerOffsetClass : ''} flex-grow w-full relative z-0`}>
                    {children}
                </main>

                <LoginModal
                    isOpen={isLoginModalOpen}
                    closeModal={() => setIsLoginModalOpen(false)}
                    openRegister={openRegister}
                    openForgotPassword={openForgotPassword}
                />

                <RegisterModal
                    isOpen={isRegisterModalOpen}
                    closeModal={() => setIsRegisterModalOpen(false)}
                    openLogin={openLogin}
                />

                <ForgotPasswordModal
                    isOpen={isForgotPasswordModalOpen}
                    closeModal={() => setIsForgotPasswordModalOpen(false)}
                    openLogin={openLogin}
                />

                {showFooter && (
                    <div className="shrink-0 relative z-10">
                        <Footer />
                    </div>
                )}
                <Toaster
                    position="top-right"
                    containerStyle={{ zIndex: 200000 }}
                    toastOptions={{ style: { zIndex: 200000 } }}
                />
            </div>
        </ModalContext.Provider>
    );
}
